import { Injectable } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CustomAlertComponent, AlertType } from '../components/custom-alert/custom-alert.component';

@Injectable({ providedIn: 'root' })
export class CustomAlertService {
  private dialogRef: DynamicDialogRef | null = null;

  constructor(private dialogService: DialogService) {}

  private open(type: AlertType, title: string, message?: string, options?: any): Promise<any> {
    this.close();

    // Detect if message contains HTML tags
    const isHtml = message && /<[^>]*>/g.test(message);

    this.dialogRef = this.dialogService.open(CustomAlertComponent, {
      data: {
        type,
        title,
        message: isHtml ? '' : (message || ''),
        html: isHtml ? message : null,
        showCancel: !!options?.showCancel,
        confirmText: options?.confirmText || (type === 'confirm' ? 'Aceptar' : 'Cerrar'),
        cancelText: options?.cancelText || 'Cerrar'
      },
      header: ' ', // Empty header, we'll use our custom one
      modal: true,
      dismissableMask: false,
      closable: false,
      styleClass: `custom-alert-dialog custom-alert-${type}`,
      width: '540px',
      contentStyle: { padding: '0', overflow: 'visible' },
      baseZIndex: 10000
    });

    return new Promise((resolve) => {
      this.dialogRef?.onClose.subscribe((result: any) => {
        // Clear reference and cleanup backdrop
        if (this.dialogRef) {
          this.dialogRef.destroy();
          this.dialogRef = null;
        }
        resolve(result);
      });
    });
  }

  showError(title: string, message?: string) {
    return this.open('error', title, message, { confirmText: 'Cerrar' });
  }

  showSuccess(title: string, message?: string) {
    console.log('showSuccess called:', title, message);
    return this.open('success', title, message);
  }

  showInfo(title: string, message?: string) {
    return this.open('info', title, message, { confirmText: 'Cerrar' });
  }

  showWarning(title: string, message?: string) {
    return this.open('warning', title, message, { showCancel: true, confirmText: 'Entendido' });
  }

  confirm(title: string, message?: string) {
    return this.open('confirm', title, message, { showCancel: true, confirmText: 'Aceptar', cancelText: 'Cancelar' });
  }

  showLoading(title: string, message?: string) {
    this.close();

    this.dialogRef = this.dialogService.open(CustomAlertComponent, {
      data: {
        type: 'loading',
        title,
        message: message || '',
        loading: true
      },
      header: ' ',
      modal: true,
      dismissableMask: false,
      closable: false,
      styleClass: 'custom-alert-dialog custom-alert-loading',
      width: '540px',
      contentStyle: { padding: '0', overflow: 'visible' },
      baseZIndex: 10000
    });

    return this.dialogRef;
  }

  /** true mientras hay un diálogo (alerta/confirmación/loading) abierto. */
  get isOpen(): boolean {
    return !!this.dialogRef;
  }

  close() {
    try {
      if (this.dialogRef) {
        this.dialogRef.close();
        this.dialogRef = null;
      }
    } catch (e) {
      this.dialogRef = null;
    }
  }

  closeLoadingAndShowSuccess(title: string, message?: string) {
    console.log('closeLoadingAndShowSuccess called:', title, message);
    this.close();
    console.log('Loading closed, showing success in 300ms...');
    // Increased delay to ensure the loading modal is fully closed before showing success
    setTimeout(() => {
      console.log('Showing success modal:', title, message);
      this.showSuccess(title, message);
    }, 300);
  }

  closeLoadingAndShowError(title: string, message?: string) {
    this.close();
    // Small delay to ensure the loading modal is fully closed before showing error
    setTimeout(() => {
      this.showError(title, message);
    }, 150);
  }
}
